import { Component, EventEmitter, Input, Output } from "@angular/core";
import { CommonModule } from "@angular/common";
import { AngularSvgIconModule } from "angular-svg-icon";

export type ModalVariant = "default" | "confirmation" | "edit" | "custom";

/**
 * Responsive width preset. Each tier is a per-breakpoint column-inset ladder
 * (a `@media` shape), so — unlike the `variant` presets, which are scalar
 * values driven by inline `[style.*]` bindings — the ladder itself lives in
 * base-modal.component.scss as `.size-compact` / `.size-standard` / `.size-wide`,
 * not here.
 */
export type ModalSize = "compact" | "standard" | "wide";

/**
 * Height preset. Unlike `size` (a per-breakpoint width ladder), each tier is a
 * flat viewport-height value that sets a *definite* height on the card, so the
 * flex `.modal-body` becomes the internal scroller while the header and actions
 * stay pinned. The values live in base-modal.component.scss as
 * `.height-tall` / `.height-standard` / `.height-small`.
 *
 * `auto` (the default) sets no height — the card is content-sized, capped by the
 * existing `max-height: 95dvh`, which is the prior behaviour of every modal.
 */
export type ModalHeight = "tall" | "standard" | "small" | "auto";

interface VariantPreset {
  gradientFrom: string;
  gradientTo: string;
  backgroundColor: string;
  titleColor: string;
  titleFontWeight: string;
  titleFontSize?: string;
  closeIconColor: string;
  closeIconOpacity: number | string;
}

const VARIANT_PRESETS: Record<ModalVariant, VariantPreset> = {
  default: {
    gradientFrom: "var(--color-evergreen)",
    gradientTo: "transparent",
    backgroundColor: "var(--color-parchment)",
    titleColor: "var(--color-parchment)",
    titleFontWeight: "600",
    closeIconColor: "var(--color-parchment)",
    closeIconOpacity: "var(--opacity-close-icon)",
  },
  confirmation: {
    gradientFrom: "var(--color-riverbed)",
    gradientTo: "transparent",
    backgroundColor: "var(--color-parchment)",
    titleColor: "var(--color-parchment)",
    titleFontWeight: "600",
    titleFontSize: "25px",
    closeIconColor: "var(--color-parchment)",
    closeIconOpacity: "var(--opacity-close-icon)",
  },
  edit: {
    gradientFrom: "var(--color-evergreen)",
    gradientTo: "transparent",
    backgroundColor: "var(--color-parchment)",
    titleColor: "var(--color-parchment)",
    titleFontWeight: "600",
    titleFontSize: "25px",
    closeIconColor: "var(--color-parchment)",
    closeIconOpacity: "var(--opacity-close-icon)",
  },
  // Matches the component's historical explicit defaults — existing callers that
  // don't set variant continue to get the same rendering as before.
  custom: {
    gradientFrom: "var(--color-evergreen)",
    gradientTo: "transparent",
    backgroundColor: "var(--color-parchment)",
    titleColor: "var(--color-evergreen)",
    titleFontWeight: "700",
    closeIconColor: "var(--color-evergreen)",
    closeIconOpacity: "var(--opacity-close-icon)",
  },
};

/**
 * New-UI (BCAD-529) successor to DialogDefaultModalComponent: the visual shell
 * for dialogs ported from the Make/Figma BaseModal design (BCAD-533). Embed it
 * in a dialog component's template and open that component via MatDialog:
 *
 *   this.dialog.open(MyNewUiDialogComponent, {
 *     panelClass: "base-modal-panel",
 *     backdropClass: "base-modal-backdrop",
 *   });
 *
 * The MatDialog backdrop provides the full-screen black/50% overlay and
 * backdrop-click close; the × button emits (closed) and the host component
 * calls dialogRef.close() — the same division of responsibility as
 * DialogDefaultModalComponent's actionClicked.
 *
 * Body content is projected via the default slot; footer buttons via
 * <... actions>. Inputs inside the body should use the .pill-input class.
 *
 * ## Variants
 * Pass `variant="confirmation"` (riverbed gradient) or `variant="edit"`
 * (evergreen gradient) for the two standard parchment/Montserrat-Alternates
 * 25px/600-weight title presets — prefer using them via
 * `ConfirmationModalComponent` / `EditModalComponent` rather than directly.
 * `variant="default"` is the legacy evergreen preset used by dialogs that
 * predate the confirmation/edit split. Any individual styling input overrides
 * the preset. Omitting `variant` (or passing `variant="custom"`) gives the
 * same behaviour as the legacy API where all styling is driven by individual
 * inputs.
 */
@Component({
  selector: "app-base-modal",
  templateUrl: "./base-modal.component.html",
  styleUrls: ["./base-modal.component.scss"],
  standalone: true,
  imports: [CommonModule, AngularSvgIconModule],
})
export class BaseModalComponent {
  // ── Variant ────────────────────────────────────────────────────────────────
  /** Applies a Figma-spec style preset. Individual styling inputs override it. */
  @Input() variant: ModalVariant = "custom";

  // ── Styling inputs (all optional — undefined falls back to the variant preset) ──
  @Input() backgroundColor?: string;
  @Input() titleColor?: string;
  @Input() gradientFrom?: string;
  @Input() gradientTo?: string;
  @Input() closeIconColor?: string;
  @Input() closeIconOpacity?: number | string;
  /** Overrides the title font size (e.g. `"18px"`). Omit to use the SCSS default. */
  @Input() titleFontSize?: string;
  /** Overrides the title font weight. Omit to use the SCSS default. */
  @Input() titleFontWeight?: string;
  /**
   * A CSS gradient string (e.g. `"linear-gradient(to bottom, #a, #b)"`) applied
   * to the entire modal root so the gradient flows continuously across both the
   * header and body. The header background becomes transparent to let it show
   * through. Takes precedence over `backgroundColor`.
   */
  @Input() gradientBackground?: string;
  /** Blur radius applied to the sticky header when `gradientBackground` is set,
   *  so scrolled body content does not bleed through. Defaults to `8px`. Pass
   *  `"0px"` to disable. */
  @Input() backdropBlur: string = "0px";

  // ── Non-styling inputs (unchanged) ─────────────────────────────────────────
  @Input() title: string = "";
  @Input() showCloseIcon: boolean = true;
  /**
   * Responsive width preset — a column-inset ladder that grows with the
   * viewport (see .size-* rulesets in base-modal.component.scss). Ignored
   * whenever `width` is explicitly set, which takes full precedence.
   */
  @Input() size: ModalSize = "standard";
  /** Raw CSS width, e.g. `"500px"`. Overrides `size` entirely when set. */
  @Input() width?: string;
  /**
   * Height preset — `tall` / `standard` / `small` give the card a definite
   * viewport-height (see .height-* rulesets in base-modal.component.scss) so its
   * body scrolls internally; `auto` (default) leaves it content-sized, capped at
   * max-height: 95dvh — the historical behaviour.
   */
  @Input() height: ModalHeight = "auto";
  /** Set to true when projecting a custom [header] slot instead of using the default text title. */
  @Input() hasCustomHeader: boolean = false;
  /** Header is pinned to the top of the scroll area by default. Set false to let it scroll with the body. */
  @Input() stickyHeader: boolean = true;

  @Output() closed = new EventEmitter<void>();

  // ── Computed effective values ───────────────────────────────────────────────
  private get preset(): VariantPreset {
    return VARIANT_PRESETS[this.variant];
  }

  get effectiveBackgroundColor(): string {
    return this.backgroundColor ?? this.preset.backgroundColor;
  }
  get effectiveTitleColor(): string {
    return this.titleColor ?? this.preset.titleColor;
  }
  get effectiveGradientFrom(): string {
    if (this.gradientBackground !== undefined && this.gradientFrom === undefined) return "transparent";
    return this.gradientFrom ?? this.preset.gradientFrom;
  }
  get effectiveGradientTo(): string {
    if (this.gradientBackground !== undefined && this.gradientTo === undefined) return "transparent";
    return this.gradientTo ?? this.preset.gradientTo;
  }
  get effectiveTitleFontWeight(): string {
    return this.titleFontWeight ?? this.preset.titleFontWeight;
  }
  get effectiveTitleFontSize(): string | undefined {
    return this.titleFontSize ?? this.preset.titleFontSize;
  }
  get effectiveCloseIconColor(): string {
    return this.closeIconColor ?? this.preset.closeIconColor;
  }
  get effectiveCloseIconOpacity(): number | string {
    return this.closeIconOpacity ?? this.preset.closeIconOpacity;
  }

  // ── Full-gradient helpers ───────────────────────────────────────────────────
  get cssFullGradient(): string | null {
    return this.gradientBackground ?? null;
  }
  get cssHeaderBg(): string | null {
    return this.gradientBackground ? "transparent" : null;
  }
  get cssHeaderBackdrop(): string | null {
    return this.gradientBackground ? `blur(${this.backdropBlur})` : null;
  }
}
